// Wallet client bootstrap.
//
// Reown AppKit + @reown/appkit-adapter-wagmi register web components that
// reference HTMLElement at module scope. Statically importing them crashes
// the Cloudflare Worker SSR runtime with
// "ReferenceError: HTMLElement is not defined".
//
// So this module keeps only SSR-safe re-exports (network metadata), and
// exposes an async `initWallet()` that dynamically imports the reown /
// wagmi-adapter modules in the browser and creates the AppKit modal +
// wagmi config on demand.

import "./buffer-polyfill";
import type { Config } from "wagmi";
import { reportSsrModuleEval } from "./error-capture";
import { reportWalletError } from "./wallet-diagnostics";
import logoAsset from "@/assets/robotdex-logo.jpg.asset.json";

export { networks } from "./wallet-networks";
export type { NetworkMeta } from "./wallet-networks";

let cachedConfig: Config | null = null;
let inflight: Promise<Config> | null = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let appkitInstance: any = null;
let appkitReady: Promise<unknown> | null = null;

// This app uses its own wallet signatures for actions. Reown Cloud's optional
// SIWX / Reown Authentication layer can be remotely enabled per project and can
// leave a stale WalletConnect auth session behind, which surfaces as
// "Forbidden: Session verification failed" after logout + reconnect. Force that
// layer off before and after AppKit bootstraps, and patch the WC controller
// prototype so newly-created connector instances cannot call authenticate().
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function disableReownAuthentication(adapter?: any) {
  try {
    const { OptionsController, WalletConnectConnector } = await import("@reown/appkit-controllers");
    OptionsController.setSIWX(undefined);
    OptionsController.setFeatures({
      ...(OptionsController.state.features ?? {}),
      email: false,
      socials: false,
      reownAuthentication: false,
    } as any);
    OptionsController.setRemoteFeatures({
      email: false,
      socials: false,
      emailCapture: false,
      reownAuthentication: false,
    } as any);

    (WalletConnectConnector as any).prototype.authenticate = async () => false;
    const connectors = [
      ...(adapter?.connectors ?? []),
      ...(adapter?.availableConnectors ?? []),
    ];
    for (const connector of connectors) {
      if (connector?.id === "walletConnect" && typeof connector.authenticate === "function") {
        connector.authenticate = async () => false;
      }
    }
  } catch (error) {
    reportWalletError(error, { context: "disableReownAuthentication" });
  }
}

export function getWagmiConfig(): Config {
  if (!cachedConfig) {
    throw new Error("Wallet not initialized yet; call initWallet() on the client first.");
  }
  return cachedConfig;
}

// SSR-safe wrapper around AppKit's modal.open(). No-op if not initialized yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function openAppKit(opts?: any): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    // Ensure init has started (button is clickable before init completes).
    if (!appkitInstance) await initWallet();
    await appkitReady;
    if (appkitInstance && typeof appkitInstance.open === "function") {
      await appkitInstance.open(opts);
    }
  } catch (error) {
    reportWalletError(error);
    throw error;
  }
}


// SSR-safe wrapper around AppKit's modal.close(). No-op if not initialized.
export async function closeAppKit(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    await appkitReady;
    if (appkitInstance && typeof appkitInstance.close === "function") {
      await appkitInstance.close();
    }
  } catch (error) {
    reportWalletError(error, { context: "closeAppKit" });
  }
}

export async function initWallet(): Promise<Config> {
  if (cachedConfig) return cachedConfig;
  // Guard with import.meta.env.SSR (Vite statically replaces this per
  // bundle). On the SSR bundle the whole browser block below becomes
  // `if (false) { ... }` and Rollup drops the dynamic imports of
  // @reown/appkit entirely — otherwise Nitro's Cloudflare Worker output
  // inlines those chunks and appkit's module-level web-component
  // registration runs in global scope (fetch/random/setTimeout disallowed).
  if (!import.meta.env.SSR) {
    if (inflight) return inflight;

    inflight = (async () => {
      const [{ createAppKit }, { WagmiAdapter }, appkitNetworks, cfg] = await Promise.all([
        import("@reown/appkit/react"),
        import("@reown/appkit-adapter-wagmi"),
        import("@reown/appkit/networks"),
        import("@/config/robotdex"),
      ]);

      const { mainnet, bsc, arbitrum, base, defineChain } = appkitNetworks;

      const robinhoodChain = defineChain({
        id: cfg.ROBINHOOD_CHAIN.id,
        caipNetworkId: `eip155:${cfg.ROBINHOOD_CHAIN.id}`,
        chainNamespace: "eip155",
        name: cfg.ROBINHOOD_CHAIN.name,
        nativeCurrency: cfg.ROBINHOOD_CHAIN.nativeCurrency,
        rpcUrls: cfg.ROBINHOOD_CHAIN.rpcUrls,
        blockExplorers: cfg.ROBINHOOD_CHAIN.blockExplorers,
        testnet: false,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const appkitNets: any = [robinhoodChain, mainnet, bsc, base, arbitrum];

      const adapter = new WagmiAdapter({
        networks: appkitNets,
        projectId: cfg.WALLETCONNECT_PROJECT_ID,
        // ssr:true is required by reown when the app tree is rendered under
        // an SSR framework (TanStack Start). Without it the WalletConnect
        // connector is registered lazily and the modal reports
        // "WalletConnectConnector not found" the moment the user picks a wallet.
        ssr: true,
      });

      await disableReownAuthentication(adapter);

      const providerErrors: string[] = [];
      const originalConsoleError = console.error;
      console.error = (...args) => {
        const text = args
          .map((arg) => (arg instanceof Error ? `${arg.message}\n${arg.stack ?? ""}` : String(arg)))
          .join(" ");
        if (/WalletConnect|W3MFrame|So\.default|Cannot create provider|connector/i.test(text)) {
          providerErrors.push(text);
        }
        originalConsoleError(...args);
      };

      try {
        appkitInstance = createAppKit({
          adapters: [adapter],
          networks: appkitNets,
          defaultNetwork: base,
          projectId: cfg.WALLETCONNECT_PROJECT_ID,
          metadata: {
            name: "RobotDex",
            description: "RobotDex — NFT Marketplace",
            url: cfg.APP_URL,
            icons: [cfg.APP_URL + logoAsset.url],
            redirect: {
              universal: cfg.APP_URL,
              linkMode: false,
            },
          } as any,
          // Show the full wallet catalog with search, not just a handful.
          featuredWalletIds: [],
          allWallets: "SHOW",
          showWallets: true,
          enableWallets: true,
          enableWalletConnect: true,
          enableInjected: true,
          enableEIP6963: true,
          enableCoinbase: true,
          enableAuthLogger: true,
          enableReconnect: false,
          enableMobileFullScreen: true,
          experimental_preferUniversalLinks: false,
          // Explicitly disable SIWX (Sign-In With X) at construction time so
          // AppKit never prompts the user to sign a "prove you own this
          // wallet" message on connect OR on reconnect after a page reload.
          // Passing `false` here is the only way to prevent AppKit from
          // triggering the SIWX flow before `ready()` resolves.
          siwx: false as any,
          features: { analytics: false, email: false, socials: false, reownAuthentication: false },
          themeMode: "dark",
          themeVariables: {
            "--w3m-accent": "#00b8ff",
            "--w3m-color-mix": "#00b8ff",
            "--w3m-color-mix-strength": 12,
            "--w3m-border-radius-master": "2px",
            "--w3m-font-family":
              "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
            "--w3m-font-size-master": "10px",
          },

        });

        // Hide the "UX by Reown" / WalletConnect watermark from the modal.
        // AppKit renders it inside Shadow DOM, so we walk open shadow roots.
        try {
          const { hideReownWatermark } = await import("./hide-watermark");
          hideReownWatermark();
        } catch {
          /* non-critical */
        }

        // Clear again after createAppKit has applied local/remote options.
        await disableReownAuthentication(adapter);

        // AppKit registers the WalletConnect connector asynchronously after
        // the universal provider is created. Don't block the UI on it: expose
        // the wagmi config immediately and let the rest finish in the
        // background. `openAppKit()` awaits `appkitReady` before opening, so
        // clicking early still works — it just waits at that point.
        appkitReady = (async () => {
          if (typeof appkitInstance?.ready === "function") {
            await appkitInstance.ready();
          }
          await disableReownAuthentication(adapter);

          // Poll briefly in case `ready()` resolved before the
          // universal-provider hook that registers the WalletConnect
          // connector fired (it is added inside
          // `WagmiAdapter.setUniversalProvider()`).
          const hasWc = () =>
            adapter.wagmiConfig.connectors.some((c) => c.id === "walletConnect");
          const deadline = Date.now() + 5000;
          while (!hasWc() && Date.now() < deadline) {
            await new Promise((r) => setTimeout(r, 100));
          }
          if (!hasWc()) {
            const detail = providerErrors.at(-1);
            throw new Error(
              detail
                ? `WalletConnect connector did not initialize. Provider error: ${detail}`
                : "WalletConnect connector did not initialize. Please refresh and try again.",
            );
          }
        })();
        appkitReady.catch((error) => reportWalletError(error, { context: "appkitReady" }));
      } finally {
        console.error = originalConsoleError;
      }

      cachedConfig = adapter.wagmiConfig as unknown as Config;
      return cachedConfig;

    })().catch((error) => {
      reportWalletError(error);
      inflight = null;
      throw error;
    });

    return inflight;
  }
  reportSsrModuleEval("initWallet() entered SSR branch", { hasWindow: typeof window !== "undefined" });
  throw new Error("initWallet() must run in the browser");
}
